import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ApiError } from '../lib/api';
import type { BootstrapData, PublicSystemSettings, User } from '../types';

export const useAppStore = defineStore('app', () => {
  const bootstrap = ref<BootstrapData | null>(null);
  const user = ref<User | null>(null);
  const initialized = ref(false);
  const loading = ref(false);
  const publicSettings = ref<PublicSystemSettings>({
    projectName: '互说知识库',
    logoUrl: null,
    passwordResetEnabled: false,
  });
  const publicSettingsInitialized = ref(false);
  const publicSettingsLoading = ref(false);

  const canManage = computed(() => user.value?.role === 'admin');
  const canEdit = computed(
    () =>
      canManage.value ||
      (bootstrap.value?.knowledgeBases ?? []).some((base) =>
        ['admin', 'editor'].includes(base.accessRole),
      ),
  );

  function applyPublicSettings(settings: PublicSystemSettings) {
    publicSettings.value = settings;
    if (typeof document !== 'undefined') document.title = settings.projectName;
  }

  async function loadBootstrap(force = false) {
    if (loading.value || (initialized.value && !force)) return;
    loading.value = true;
    try {
      const data = await api.bootstrap();
      bootstrap.value = data;
      user.value = data.user;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        bootstrap.value = null;
        user.value = null;
      }
      throw error;
    } finally {
      initialized.value = true;
      loading.value = false;
    }
  }

  async function loadPublicSettings(force = false) {
    if (publicSettingsLoading.value || (publicSettingsInitialized.value && !force)) return;
    publicSettingsLoading.value = true;
    try {
      const result = await api.publicSettings();
      applyPublicSettings(result.settings);
    } finally {
      publicSettingsInitialized.value = true;
      publicSettingsLoading.value = false;
    }
  }

  function setPublicSettings(settings: PublicSystemSettings) {
    applyPublicSettings(settings);
    publicSettingsInitialized.value = true;
  }

  async function login(email: string, password: string) {
    const result = await api.login(email, password);
    user.value = result.user;
    initialized.value = false;
    await loadBootstrap(true);
  }

  async function logout() {
    await api.logout();
    bootstrap.value = null;
    user.value = null;
    initialized.value = true;
  }

  return {
    bootstrap,
    user,
    initialized,
    loading,
    publicSettings,
    publicSettingsInitialized,
    canManage,
    canEdit,
    loadBootstrap,
    loadPublicSettings,
    setPublicSettings,
    login,
    logout,
  };
});
